import { Document, Token } from "@/tools/msa/models";
import { onMounted, onUnmounted, ref } from "@vue/composition-api";
import { range } from "@/utils";

const getRanges = (selection: Selection): Range[] =>
  range(0, selection.rangeCount).map(index => selection.getRangeAt(index));

export const getSelectedTokens = (tokens: Token[]) => {
  const selection = window.getSelection();

  if (
    selection === null ||
    selection.anchorNode === null ||
    selection.anchorNode.parentElement === null ||
    selection.focusNode === null ||
    selection.focusNode.parentElement === null
  )
    return;

  const index = new Map(tokens.map(token => [token.id, token]));

  const ranges = getRanges(selection)
    .map(range => ({
      range,
      tokens: Array.from(range.cloneContents().children)
        .map(node => node.closest("[data-token]"))
        .filter(element => element instanceof HTMLElement)
        .map(element => (element as HTMLElement).dataset.token)
        .filter(Boolean)
        .map(id => index.get(id!) as Token)
    }))
    .filter(range => range.tokens.length > 0);

  if (ranges.length > 1) return;

  return ranges[0];
};

export const useTokensSelection = (props: { document: Document }) => {
  const selected = ref<Token[]>([]);
  const range = ref<Range | null>(null);

  const reset = () => {
    selected.value = [];
    range.value = null;
  };

  const listener = () => {
    const results = getSelectedTokens(props.document.tokens);

    if (results) {
      selected.value = results.tokens;
      range.value = results.range;
    } else {
      reset();
    }
  };

  onMounted(() => {
    document.addEventListener("selectionchange", listener);
  });

  onUnmounted(() => {
    document.removeEventListener("selectionchange", listener);
  });

  return { selected, range, reset };
};
